/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React / Libs
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router';

// Mantine
import {
	ActionIcon,
	Badge,
	Box,
	Flex,
	Group,
	Image,
	Paper,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { IconFilter } from '@tabler/icons-react';

// UI Components
import { Title } from '@components/v2/ui';
import { AdditionalityPoolProjectCard } from '@components/v2/ui/AdditionalityPoolProjectCard/AdditionalityPoolProjectCard';
import { AdditionalityProjectCard } from '@components/v2/ui/AdditionalityProjectCard';
import { Pagination } from '@components/v2/ui/Pagination';
import { ProjectCardSkeleton } from '@components/v2/ui/ProjectCard/ProjectCardSkeleton';

// Features
import { useAdditionalityProjects as useAdditionalityProjectsApi } from '@features/catalogs/additionality-projects/list/api/get-additionality-projects-public';
import { Filter } from '@features/catalogs/additionality-projects/list/components/Filter-public-catalog';
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/list/hooks/useAdditionalityProjects';

// Constants / Types / Utils
import { NOT_APPLICABLE } from '@config/constants/common';
import { TAdditionalityProject } from '@global-types/api/api';
import { interpolate } from '@utils/functions';

// Styles
import { EProjectTechnology } from '@global-types/api/enum';
import classes from './additionality-projects.module.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const AdditionalityProjectsPublic = () => {
	/* ********************************** Hooks ********************************* */
	const { t } = useTranslation();

	// Routing
	const navigate = useNavigate();

	const {
		handleChangePaginationSize,
		handleChangePagination,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
	} = useAdditionalityProject();

	// Fetch additionality projects data
	const {
		data: additionalityProjects,
		isPending,
		total: totalItems,
		totalPages,
	} = useAdditionalityProjectsApi(
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams
	);

	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);
	const [filtersNbr, setFiltersNbr] = useState(0);

	/* ******************************** RENDERING ******************************* */
	return (
		<Paper className={classes['main-layout']}>
			<Filter
				opened={openedFilters}
				onClose={closeFilters}
				title={t('filters.titles.projects')}
				onApplyFilters={(filters) => {
					setFiltersNbr(Object.keys(filters).length);
				}}
				value={additionalityProjectsQueryParams}
			/>
			<Box className={classes.header}>
				<Title
					c="var(--mantine-color-igluPrimary-9)"
					order={4}
					textWrap="wrap"
					data-testid="topbar-title"
				>
					{t('global.additionalities')}
				</Title>
			</Box>

			<Paper
				ml={'20px'}
				mr={'20px'}
				mt={'24px'}
				p={'12px'}
			>
				<Box className={classes['action-bar']}>
					<Flex flex={1} justify={'end'} wrap={'wrap'}>
						<Group gap={'var(--mantine-spacing-xs)'}>
							<div
								style={{
									position: 'relative',
								}}
							>
								{filtersNbr > 0 && (
									<Badge
										style={{
											position: 'absolute',
											zIndex: '3',
											right: '-3px',
										}}
										color="var(--mantine-color-igluRed-9)"
										size="xs"
										circle
									>
										{filtersNbr}
									</Badge>
								)}
								<ActionIcon
									variant="outline"
									size="48px"
									radius="xl"
									color="var(--mantine-color-igluBorder-9)"
									data-testid="bell-icon-button"
									onClick={openFilters}
									bg={
										filtersNbr
											? 'var(--mantine-color-igluPrimary-9)'
											: 'transparent'
									}
								>
									<IconFilter
										color={
											filtersNbr
												? 'var(--mantine-color-igluTextButton-9)'
												: 'var(--mantine-color-igluPrimary-9)'
										}
										radius="xl"
										size={24}
										stroke={1.5}
									/>
								</ActionIcon>
							</div>
						</Group>
					</Flex>
				</Box>
				<Box
					style={{
						display: 'grid',
						gridTemplateColumns:
							'repeat(auto-fill, minmax(clamp(300px, 50%, 643px), 1fr))',
						gap: 'var(--mantine-spacing-sm)',
					}}
					mb={'var(--mantine-spacing-sm)'}
				>
					{Array.isArray(additionalityProjects) &&
						additionalityProjects.length > 0 &&
						additionalityProjects
							.filter(
								(project: TAdditionalityProject) =>
									project?.technology ==
									EProjectTechnology.PROJECT_FOUNDER
							)
							.map(
								(
									project: TAdditionalityProject,
									index
								) => (
									<AnimatePresence
										key={index}
										mode="wait"
									>
										<motion.div
											key={'empty'}
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -10, opacity: 0 }}
											transition={{ duration: 0.2 }}
											style={{
												gridColumn:
													additionalityProjects.filter(
														(
															project: TAdditionalityProject
														) =>
															project?.technology ==
															EProjectTechnology.PROJECT_FOUNDER
													).length %
														2 !==
														0 &&
													index ===
														additionalityProjects.filter(
															(
																project: TAdditionalityProject
															) =>
																project?.technology ==
																EProjectTechnology.PROJECT_FOUNDER
														).length -
															1
														? '1 / -1'
														: 'auto',
												height: '100%',
											}}
										>
											<AdditionalityPoolProjectCard
												title={project.name}
												cover={project.cover}
												description={interpolate(
													t(
														'global.additionalityPoolDescription'
													),
													{
														nom_project: project.name,
														organization:
															project.organization,
													}
												)}
												financing={{
													unlimited:
														project?.financing?.unlimited,
													pledged_euro:
														project?.financing
															?.pledged_euro,
													target_euro:
														project?.financing
															?.target_euro,
												}}
												onLearnMore={() => {
													navigate(
														`/catalog-additionalities/${project.id}`
													);
												}}
												fullWidth={true}
												showMonetization={true}
											/>
										</motion.div>
									</AnimatePresence>
								)
							)}
				</Box>

				<Box
					style={{
						display: 'grid',
						gridTemplateColumns:
							'repeat(auto-fill, minmax(300px, 1fr))',
						gap: 'var(--mantine-spacing-sm)',
					}}
				>
					{isPending
						? [...Array(10).keys()].map(
								(_project, index) => (
									<AnimatePresence
										key={index}
										mode="wait"
									>
										<motion.div
											key={'empty'}
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -10, opacity: 0 }}
											transition={{ duration: 0.2 }}
											style={{
												display: 'flex',
												flex: 1,
											}}
										>
											<ProjectCardSkeleton
												fullWidth={true}
											/>
										</motion.div>
									</AnimatePresence>
								)
							)
						: additionalityProjects &&
							Array.isArray(additionalityProjects) &&
							additionalityProjects.length > 0 &&
							additionalityProjects
								.filter(
									(project) =>
										project?.technology !==
										EProjectTechnology.PROJECT_FOUNDER
								)
								.map((project) => (
									<AnimatePresence
										key={project.id}
										mode="wait"
									>
										<motion.div
											key={project.id}
											initial={{ y: 10, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -10, opacity: 0 }}
											transition={{ duration: 0.2 }}
											style={{
												display: 'flex',
												flex: 1,
											}}
										>
											<AdditionalityProjectCard
												cover={project?.image}
												projectType={project?.type}
												name={project?.name}
												active={project?.active}
												fullWidth={true}
												supportedByLogo={
													project?.supportedBy
												}
												location={{
													countryCode:
														project?.country?.code ||
														NOT_APPLICABLE,
													place:
														project?.location?.place ||
														NOT_APPLICABLE,
													countryName:
														project?.country?.name ||
														NOT_APPLICABLE,
												}}
												onStatusChange={undefined}
												onClick={() => {
													navigate(
														`/catalog-additionalities/${project.id}`
													);
												}}
												showStatus={false}
												fundedPortion={
													project?.financing
														?.pledged_euro || 0
												}
											/>
										</motion.div>
									</AnimatePresence>
								))}
				</Box>

				{additionalityProjects &&
					Array.isArray(additionalityProjects) &&
					additionalityProjects.length == 0 && (
						<Box className={classes.containerEmptyList}>
							<Box
								className={
									classes.containerTextsNoRecords
								}
							>
								<Image
									src={NoRecordsIcon}
									className={classes.iconNoRecords}
								/>
								<>
									<Text
										className={classes.titleNoRecords}
									>
										{t('global.datatable.noResultsFound')}
									</Text>
									<Text
										className={
											classes.descriptionNoRecords
										}
									>
										{t(
											'global.datatable.noResultsFoundDescription'
										)}
									</Text>
								</>
							</Box>
						</Box>
					)}
				<Box className={classes['pagination-bar']}>
					<Pagination
						pageSize={additionalityProjectsPageSize}
						totalItems={totalItems}
						activePage={additionalityProjectsPage}
						onPageSizeChange={handleChangePaginationSize}
						onChange={handleChangePagination}
						total={totalPages ? totalPages : 0}
					/>
				</Box>
			</Paper>
		</Paper>
	);
};

export default AdditionalityProjectsPublic;
